import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Building2, Plus, Loader2, Trash2, Store, Sparkles, Radio, Check, UserPlus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOwnersPage = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  // Assign-outlets modal
  const [assignOwner, setAssignOwner] = useState(null);
  const [assignData, setAssignData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [savingAssign, setSavingAssign] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/owners`);
      setOwners(r.data);
    } catch (e) {
      toast.error('Failed to load owners');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isGmail = form.email.trim().toLowerCase().endsWith('@gmail.com');

  const createOwner = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!isGmail && !form.password.trim()) { toast.error('Password is required for non-Gmail accounts'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/clients`, {
        name: form.name.trim(),
        email: form.email.trim(),
        password: isGmail ? '' : form.password.trim(),
        role: 'owner',
      });
      toast.success('Owner created');
      setAddOpen(false);
      setForm({ name: '', email: '', password: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create owner');
    } finally { setCreating(false); }
  };

  const deleteOwner = async (o) => {
    if (!window.confirm(`Delete owner "${o.name}"? Their outlets will be unassigned (venues are NOT deleted).`)) return;
    try {
      await axios.delete(`${API}/admin/owners/${o.id}`);
      toast.success('Owner deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const openAssign = async (o) => {
    setAssignOwner(o);
    setAssignData(null);
    try {
      const r = await axios.get(`${API}/admin/owners/${o.id}`);
      setAssignData(r.data);
      setSelectedIds(new Set(r.data.venues.filter((v) => v.assigned_to_me).map((v) => v.id)));
    } catch (e) {
      toast.error('Failed to load outlets');
      setAssignOwner(null);
    }
  };

  const toggleVenue = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await axios.put(`${API}/admin/owners/${assignOwner.id}/outlets`, { venue_ids: Array.from(selectedIds) });
      toast.success('Outlets updated');
      setAssignOwner(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSavingAssign(false); }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="admin-owners-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-[0.2em] mb-1 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-[#00FFFF]" /> Chain Owners
            </p>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-white" style={{ fontFamily: 'Outfit' }}>Owners</h1>
            <p className="text-gray-400 text-sm mt-1">Owner accounts manage a group of outlets from one dashboard.</p>
          </div>
          <Button onClick={() => setAddOpen(true)} className="bg-[#39FF14] text-black hover:bg-[#2fd60f] rounded-full font-bold h-10 px-5" data-testid="add-owner-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Owner
          </Button>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 text-gray-500 animate-spin" /></div>
        ) : owners.length === 0 ? (
          <div className="py-24 text-center" data-testid="owners-empty">
            <Building2 className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-400">No owners yet. Create one and assign their outlets.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="owners-list">
            {owners.map((o) => (
              <div key={o.id} className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-4" data-testid={`owner-row-${o.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#00FFFF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-bold truncate">{o.name}</p>
                    <p className="text-gray-500 text-xs truncate">{o.email} · {o.auth_method === 'google' ? 'Google sign-in' : 'Password'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-400 flex items-center gap-1.5 bg-black/30 px-3 py-1.5 rounded-full">
                    <Store className="w-3.5 h-3.5" /> {o.outlet_count} outlet{o.outlet_count === 1 ? '' : 's'}
                  </span>
                  <Button onClick={() => openAssign(o)} variant="ghost" className="h-8 px-3 rounded-full text-xs font-bold border border-[#00FFFF]/30 text-[#00FFFF] hover:bg-[#00FFFF]/10" data-testid={`manage-outlets-${o.id}`}>
                    Manage outlets
                  </Button>
                  <button onClick={() => deleteOwner(o)} className="text-gray-500 hover:text-[#FF3B3B] p-2" data-testid={`delete-owner-${o.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add owner dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white" data-testid="add-owner-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#39FF14]" /> New Owner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-gray-300">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10 mt-1" placeholder="e.g. Acme Hospitality Group" data-testid="owner-name-input" />
            </div>
            <div>
              <Label className="text-gray-300">Email *</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/40 border-white/10 mt-1" placeholder="owner@company.com" data-testid="owner-email-input" />
            </div>
            {!isGmail && (
              <div>
                <Label className="text-gray-300">Password *</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black/40 border-white/10 mt-1" placeholder="Set a login password" data-testid="owner-password-input" />
              </div>
            )}
            {isGmail && <p className="text-xs text-[#00FFFF]">Gmail accounts sign in with Google — no password needed.</p>}
          </div>
          <DialogFooter>
            <Button onClick={createOwner} disabled={creating} className="bg-[#39FF14] text-black hover:bg-[#2fd60f] rounded-full font-bold" data-testid="create-owner-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Owner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign outlets dialog */}
      <Dialog open={!!assignOwner} onOpenChange={(o) => { if (!o) setAssignOwner(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg" data-testid="assign-outlets-dialog">
          <DialogHeader>
            <DialogTitle>Assign outlets · {assignOwner?.name}</DialogTitle>
          </DialogHeader>
          {!assignData ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto space-y-1.5 py-1" data-testid="assign-venue-list">
              {assignData.venues.map((v) => {
                const checked = selectedIds.has(v.id);
                const ownedByOther = v.owner_id && !v.assigned_to_me;
                return (
                  <button
                    key={v.id}
                    onClick={() => toggleVenue(v.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${checked ? 'border-[#39FF14]/50 bg-[#39FF14]/5' : 'border-white/10 hover:bg-white/5'}`}
                    data-testid={`assign-venue-${v.id}`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#39FF14]' : 'border border-white/20'}`}>
                      {checked && <Check className="w-3.5 h-3.5 text-black" />}
                    </div>
                    {v.client_type === 'ai' ? <Sparkles className="w-4 h-4 text-[#00FFFF] flex-shrink-0" /> : <Radio className="w-4 h-4 text-[#FF007F] flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{v.name}</p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {v.client_type === 'ai' ? 'AI' : 'Commercial'}
                        {ownedByOther && <span className="text-[#FFB300] ml-1.5">· currently owned by {v.owner_name} (will move here)</span>}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveAssign} disabled={savingAssign || !assignData} className="bg-[#39FF14] text-black hover:bg-[#2fd60f] rounded-full font-bold" data-testid="save-assign-btn">
              {savingAssign ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save (${selectedIds.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOwnersPage;
